import React, { useEffect, useMemo, useState } from "react";
import { useLang } from "../../context/LangContext";

const DEFAULT_VALUES = {
  necessary: true,
  preferences: false,
  analytics: false,
  marketing: false,
};

export default function CookieSettings({
  open,
  values,
  onClose,
  onSave,
  onAcceptAll,
  onRejectAll,
}) {
  const { dict } = useLang();
  const cookieDict = dict?.cookie ?? {};

  const text = {
    title: cookieDict.settingsTitle ?? "Çerez Tercihleri",
    description:
      cookieDict.settingsDescription ??
      "Kene Teknoloji olarak, web sitemizde kullanılan çerezleri dilediğiniz zaman yönetebilirsiniz. Tercihlerinizi düzenledikten sonra kaydedebilirsiniz.",
    preferencesTitle: cookieDict.preferencesTitle ?? "İşlevsellik Çerezleri",
    preferencesText:
      cookieDict.preferencesText ??
      "Bu çerezler dil tercihi, kullanıcı tercihleri ve site deneyiminin iyileştirilmesi için kullanılır.",
    necessaryTitle: cookieDict.necessaryTitle ?? "Gerekli / Zorunlu Çerezler",
    necessaryText:
      cookieDict.necessaryText ??
      "Bu çerezler sitenin düzgün çalışması için zorunludur. Oturum, güvenlik ve temel sayfa işlevleri için kullanılır ve kapatılamaz.",
    marketingAnalyticsTitle:
      cookieDict.marketingAnalyticsTitle ?? "Pazarlama & Analitik Çerezleri",
    marketingAnalyticsText:
      cookieDict.marketingAnalyticsText ??
      "Bu çerezler site performansını ölçmek, kullanım eğilimlerini anlamak ve onay vermeniz halinde daha ilgili içerik sunmak için kullanılabilir.",
    save: cookieDict.save ?? "Kaydet",
    acceptAll: cookieDict.acceptAll ?? "Kabul Et",
    rejectAll: cookieDict.rejectOptional ?? "Reddet",
    alwaysActive: cookieDict.alwaysActive ?? "Her zaman aktif",
    closeAria: cookieDict.closeAria ?? "Kapat",
  };

  const [localValues, setLocalValues] = useState(DEFAULT_VALUES);

  useEffect(() => {
    if (!open) return;

    setLocalValues({
      ...DEFAULT_VALUES,
      ...values,
      necessary: true,
    });
  }, [open, values]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  const marketingAnalyticsEnabled = useMemo(
    () => Boolean(localValues.analytics || localValues.marketing),
    [localValues.analytics, localValues.marketing]
  );

  const togglePreferences = () => {
    setLocalValues((prev) => ({
      ...prev,
      preferences: !prev.preferences,
    }));
  };

  const toggleMarketingAnalytics = () => {
    setLocalValues((prev) => {
      const next = !(prev.analytics || prev.marketing);
      return {
        ...prev,
        analytics: next,
        marketing: next,
      };
    });
  };

  const handleSave = () => {
    onSave({
      ...localValues,
      necessary: true,
    });
  };

  if (!open) return null;

  return (
    <div className="cookie-settings-overlay" onClick={onClose}>
      <div
        className="cookie-settings"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-settings-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cookie-settings__header">
          <div>
            <h2 id="cookie-settings-title" className="cookie-settings__title">
              {text.title}
            </h2>
            <p className="cookie-settings__description">{text.description}</p>
          </div>

          <button
            type="button"
            className="cookie-settings__close"
            onClick={onClose}
            aria-label={text.closeAria}
          >
            ×
          </button>
        </div>

        <div className="cookie-settings__list">
          <div className="cookie-settings__item">
            <div className="cookie-settings__item-text">
              <h3>{text.preferencesTitle}</h3>
              <p>{text.preferencesText}</p>
            </div>

            <button
              type="button"
              className={`cookie-switch ${localValues.preferences ? "is-on" : ""}`}
              aria-pressed={localValues.preferences}
              onClick={togglePreferences}
            >
              <span className="cookie-switch__thumb" />
            </button>
          </div>

          <div className="cookie-settings__item is-locked">
            <div className="cookie-settings__item-text">
              <h3>{text.necessaryTitle}</h3>
              <p>{text.necessaryText}</p>
            </div>

            <div className="cookie-switch cookie-switch--locked">
              <span>{text.alwaysActive}</span>
            </div>
          </div>

          <div className="cookie-settings__item">
            <div className="cookie-settings__item-text">
              <h3>{text.marketingAnalyticsTitle}</h3>
              <p>{text.marketingAnalyticsText}</p>
            </div>

            <button
              type="button"
              className={`cookie-switch ${marketingAnalyticsEnabled ? "is-on" : ""}`}
              aria-pressed={marketingAnalyticsEnabled}
              onClick={toggleMarketingAnalytics}
            >
              <span className="cookie-switch__thumb" />
            </button>
          </div>
        </div>

        <div className="cookie-settings__actions">
          <button
            type="button"
            className="cookie-settings__btn cookie-settings__btn--soft"
            onClick={onAcceptAll}
          >
            {text.acceptAll}
          </button>

          <button
            type="button"
            className="cookie-settings__btn cookie-settings__btn--ghost"
            onClick={onRejectAll}
          >
            {text.rejectAll}
          </button>

          <button
            type="button"
            className="cookie-settings__btn cookie-settings__btn--primary"
            onClick={handleSave}
          >
            {text.save}
          </button>
        </div>
      </div>
    </div>
  );
}